import * as React from "react";
import { dataProvider } from "@/providers/dataProvider";
import type { AdminLocality } from "@/pages/localities/types";
import { activeFrames } from "@/pages/localities/types";

// ---------------------------------------------------------------------------
// Types — mirror GET /admin/localities/candidates
// ---------------------------------------------------------------------------

export type LocalityCandidate = {
  viewKind: "banner" | "main" | "night" | "panorama";
  displayRef: string;
  thumbnailRef: string | null;
  license: string;
  licenseUrl: string | null;
  attribution: string | null;
  width: number | null;
  height: number | null;
  selectable: boolean;
  rejectionReason: string | null;
};

interface CurateLocalityModalProps {
  locality: AdminLocality;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Curation dialog: left is what the locality shows today, right is what Wikidata offers for
 * a given entity. Nothing is written until "Сохранить" — the candidates request is read-only.
 *
 * Unusable candidates stay visible but disabled with their reason attached, instead of being
 * filtered out: an operator who saw a picture on Wikidata and cannot find it here would
 * otherwise have no way to learn that it was dropped for a missing license.
 */
export function CurateLocalityModal({
  locality,
  onClose,
  onSaved,
}: CurateLocalityModalProps): React.JSX.Element {
  const [qid, setQid] = React.useState("");
  const [candidates, setCandidates] = React.useState<LocalityCandidate[] | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const current = activeFrames(locality.frames);

  async function loadCandidates(): Promise<void> {
    const trimmed = qid.trim();
    if (!/^Q\d+$/.test(trimmed)) {
      setError("QID должен выглядеть как Q12345");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (!dataProvider.custom) throw new Error("dataProvider.custom not implemented");
      const response = await dataProvider.custom<{ candidates: LocalityCandidate[] }>({
        url: `/admin/localities/candidates?qid=${encodeURIComponent(trimmed)}`,
        method: "get",
      });
      setCandidates(response.data.candidates);
      setSelected(new Set());
    } catch {
      setError("Не удалось получить кандидатов");
    } finally {
      setBusy(false);
    }
  }

  function toggle(candidate: LocalityCandidate): void {
    if (!candidate.selectable) return;
    const next = new Set(selected);
    // One frame per view kind: the carousel is ordered by view kind, and the server rejects
    // duplicates — enforcing it here keeps the rejection from arriving as a surprise on save.
    const sameKind = (candidates ?? []).find(
      (other) => other.viewKind === candidate.viewKind && next.has(other.displayRef),
    );
    if (next.has(candidate.displayRef)) {
      next.delete(candidate.displayRef);
    } else {
      if (sameKind !== undefined) next.delete(sameKind.displayRef);
      next.add(candidate.displayRef);
    }
    setSelected(next);
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (!dataProvider.custom) throw new Error("dataProvider.custom not implemented");
      const frames = (candidates ?? [])
        .filter((candidate) => selected.has(candidate.displayRef))
        .map((candidate) => ({
          viewKind: candidate.viewKind,
          displayRef: candidate.displayRef,
          thumbnailRef: candidate.thumbnailRef,
          license: candidate.license,
          licenseUrl: candidate.licenseUrl,
          attribution: candidate.attribution,
          width: candidate.width,
          height: candidate.height,
        }));
      await dataProvider.custom({
        url: `/admin/localities/${locality.id}/media`,
        method: "put",
        payload: { kind: locality.kind, frames },
      });
      onSaved();
    } catch {
      setError("Не удалось сохранить подборку");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Картинки локации ${locality.name}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="flex max-h-[86vh] w-full max-w-[900px] flex-col gap-4 overflow-auto rounded-lg bg-[var(--surface)] p-5">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[17px] font-[600]">{locality.name}</h2>
            <p className="text-[12px] text-[var(--muted)]">
              {locality.kind === "country" ? "страна" : "город"}
              {locality.countryIsoCode !== null ? ` · ${locality.countryIsoCode}` : ""}
            </p>
          </div>
          <button type="button" className="h-8 rounded-[var(--radius-sm)] border border-[var(--border)] px-3 text-[13px]" onClick={onClose}>
            Закрыть
          </button>
        </header>

        <section className="flex flex-col gap-2">
          <h3 className="text-[13px] font-[600]">Сейчас показывается</h3>
          {current.length === 0 ? (
            <p className="text-[13px] text-[var(--muted)]">Картинок нет</p>
          ) : (
            <ul className="flex gap-2 overflow-x-auto">
              {current.map((frame) => (
                <li key={frame.id} className="w-[160px] flex-shrink-0">
                  <img
                    src={frame.thumbnailRef ?? frame.displayRef}
                    alt=""
                    className="h-[90px] w-full rounded-md object-cover"
                    loading="lazy"
                  />
                  <span className="text-[11px] text-[var(--muted)]">
                    {frame.viewKind ?? "—"} · {frame.sourceProvider}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-[13px] font-[600]">Предложенные ссылки</h3>
          <div className="flex gap-2">
            <input
              aria-label="Wikidata QID"
              className="h-9 flex-1 rounded-[var(--radius-sm)] border border-[var(--border)] px-3 text-[13px]"
              placeholder="Wikidata QID, например Q243"
              value={qid}
              onChange={(event) => setQid(event.target.value)}
            />
            <button
              type="button"
              className="h-9 rounded-[var(--radius-sm)] border border-[var(--border)] px-3 text-[13px]"
              onClick={() => void loadCandidates()}
              disabled={busy}
            >
              Показать
            </button>
          </div>

          {error !== null ? <p className="text-[13px] text-[var(--danger)]">{error}</p> : null}

          {candidates !== null && candidates.length === 0 ? (
            <p className="text-[13px] text-[var(--muted)]">
              У этой сущности нет подходящих изображений
            </p>
          ) : null}

          {candidates !== null && candidates.length > 0 ? (
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
              {candidates.map((candidate) => {
                const checked = selected.has(candidate.displayRef);
                return (
                  <li
                    key={candidate.displayRef}
                    className={[
                      "rounded-[var(--radius-sm)] border border-[var(--border)] p-2",
                      candidate.selectable ? "" : "opacity-50",
                      checked ? "border-[var(--accent)]" : "",
                    ].join(" ")}
                  >
                    <label className="flex cursor-pointer flex-col gap-1">
                      <img
                        src={candidate.thumbnailRef ?? candidate.displayRef}
                        alt=""
                        className="h-[100px] w-full rounded object-cover"
                        loading="lazy"
                      />
                      <span className="flex items-center gap-2 text-[12px]">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!candidate.selectable}
                          onChange={() => toggle(candidate)}
                        />
                        {candidate.viewKind}
                      </span>
                      <span className="text-[11px] text-[var(--muted)]">
                        {candidate.selectable
                          ? candidate.license
                          : `нельзя выбрать: ${candidate.rejectionReason ?? "неизвестно"}`}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </section>

        <footer className="flex items-center justify-between gap-3">
          <span className="text-[12px] text-[var(--muted)]">
            Выбрано: {selected.size}. Пустая подборка вернёт автоматическую картинку.
          </span>
          <button
            type="button"
            className="h-9 rounded-md bg-[var(--accent)] px-4 text-[13px] text-white"
            onClick={() => void save()}
            disabled={busy || candidates === null}
          >
            Сохранить
          </button>
        </footer>
      </div>
    </div>
  );
}
